import { observable } from "@legendapp/state";

import { blocks$ } from "~/stores/blocks";
import { datasets$ } from "~/stores/datasets";
import type { DatasetColumnGroup } from "./board-filters/types";

/**
 * UI state for the board/dashboard page: focused block and movement state.
 * The sidepane visibility is derived from focusedBlockId (shown when non-null).
 */
export const boardPage$ = observable({
  activeBoardId: "",
  selectedBlockId: null as string | null,
  /** Set during drag/resize so click handlers can suppress accidental selection. */
  isDragging: false,
  /** Block IDs currently in "peek" mode, showing table instead of chart/statistic. */
  peekingBlockIds: {} as Record<string, boolean>,
  /** Ephemeral per-block search terms for the quick search feature. Not persisted. */
  blockSearchTerms: {} as Record<string, string>,
  /** Unique datasets (with columns) referenced by the active board's blocks. */
  datasetColumns: (): DatasetColumnGroup[] => {
    const boardId = boardPage$.activeBoardId.get();
    if (!boardId) return [];

    const allBlocks = blocks$.get() ?? {};
    const seen = new Set<string>();
    const result: DatasetColumnGroup[] = [];

    for (const block of Object.values(allBlocks)) {
      if (block.boardId !== boardId) continue;
      const datasetId = block.datasetId;
      if (!datasetId || seen.has(datasetId)) continue;
      seen.add(datasetId);

      const name = datasets$[datasetId].name.get() ?? "Unnamed dataset";
      const runId = datasets$[datasetId].runId.get();
      if (!runId) continue;

      const columns = datasets$[datasetId].columns.get();
      if (!columns || Object.keys(columns).length === 0) continue;

      const columnsConfig = datasets$[datasetId].columnsConfig.get() ?? {};
      result.push({ datasetId, datasetName: name, columns, columnsConfig });
    }

    return result;
  },
});
